
import { Project } from "@/types/Project";
import { Button } from "@/components/ui/button";
import { Archive, Clock, ExternalLink } from "lucide-react";
import { format } from "date-fns";
import { useToast } from "@/components/ui/use-toast";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface ProjectListProps {
  projects: Project[];
  onArchive: (id: string) => void;
  onSelect: (project: Project) => void;
}

export function ProjectList({ projects, onArchive, onSelect }: ProjectListProps) {
  const { toast } = useToast();
  const activeProjects = projects.filter((p) => !p.isArchived);

  const handleArchive = (project: Project) => {
    onArchive(project.id);
    toast({
      title: "Project archived",
      description: `${project.name} has been archived.`,
    });
  };

  return (
    <div className="space-y-4">
      {activeProjects.map((project) => (
        <div
          key={project.id}
          className="glass-card p-4 rounded-lg hover:shadow-md hover:border-primary/30 transition-all duration-200 hover:scale-[1.01] animate-in"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 space-y-1">
              <h3 className="font-medium">{project.name}</h3>
              {project.description && (
                <p className="text-sm text-muted-foreground">{project.description}</p>
              )}
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="h-3 w-3" />
                Created {format(project.createdAt, "PPP")}
              </div>
              <div className="text-sm text-muted-foreground">
                Rate: ${project.hourlyRate}/hour
              </div>
            </div>
            <div className="flex gap-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onSelect(project)}
                      className="transition-colors hover:border-primary/50 hover:text-primary"
                    >
                      <ExternalLink className="h-4 w-4 mr-1" />
                      View Details
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>View and edit project details</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleArchive(project)}
                      className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                    >
                      <Archive className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Archive this project</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
